import { FC, useRef, useState, useEffect, useId } from "react";
import { Box, Chip, Menu, MenuItem, Stack, TextField, Typography, Checkbox } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

type Props = {
	label: string;
	options: Array<{ label: string; value: string }>;
	value: string[];
	onChange: (v: any) => void;
};

export const FacetFilter: FC<Props> = ({ label, options, value, onChange }) => {
	const idPrefix = useId();
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);
	const [localValue, setLocalValue] = useState(value);
	const [search, setSearch] = useState("");
	const visibleOptions = search
		? options.filter((o) =>
				o.label.toLowerCase().includes(search.toLowerCase()),
			)
		: options;

	// To reset the selection after the user navigates back to the ESR list page
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	const toggleSelection = (optionValue: string) => {
		setLocalValue(
			localValue.includes(optionValue)
				? localValue.filter((val) => val !== optionValue)
				: [...localValue, optionValue].sort(),
		);
	};

	return (
		<>
			<Chip
				sx={{ maxWidth: 320 }}
				aria-haspopup="listbox"
				aria-expanded={open}
				label={
					<Stack direction="row" alignItems="center" gap="8px" minWidth={0}>
						<Typography
							fontSize={13}
							fontWeight={500}
							noWrap
							sx={{ minWidth: 0 }}
						>
							{label}:{" "}
							{value
								.map(
									(v) =>
										options.find((o) => o.value === v)?.label ?? "(empty)",
								)
								.join(" OR ") || "Any"}
						</Typography>
						{value.length === 0 ? (
							open ? (
								<ExpandLess sx={{ color: "#00000042" }} />
							) : (
								<ExpandMore sx={{ color: "#00000042" }} />
							)
						) : null}
					</Stack>
				}
				variant={value.length ? "outlined" : "filled"}
				color={value.length ? "primary" : "default"}
				ref={anchorRef}
				onClick={() => {
					if (value.length) return;
					setOpen(true);
				}}
				onDelete={
					value.length
						? () => {
							setLocalValue([]);
							onChange([]);
						}
						: undefined
				}
				disabled={options.length === 0}
			/>
			<Menu
				anchorEl={anchorRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				open={open}
				autoFocus={false}
				PaperProps={{ sx: { width: 420, maxHeight: "70vh" } }}
				MenuListProps={{ sx: { pt: 0 } }}
				onClose={() => {
					setOpen(false);
					setSearch("");
					onChange(localValue);
				}}
			>
				<Box
					sx={{
						position: "sticky",
						top: 0,
						zIndex: 1,
						bgcolor: "background.paper",
						borderBottom: "1px solid",
						borderColor: "divider",
						px: "12px",
						py: "8px",
					}}
				>
					<TextField
						autoFocus
						fullWidth
						size="small"
						placeholder={`Search ${label}`}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						// the Menu moves focus to whichever item matches typed
						// characters, which would steal it away mid-word
						onKeyDown={(e) => e.stopPropagation()}
					/>
				</Box>
				{visibleOptions.length === 0 && (
					<MenuItem disabled>No matches</MenuItem>
				)}
				{visibleOptions.map((o, index) => {
					const labelId = `${idPrefix}-${index}`;
					return (
						<MenuItem
							sx={{
								background: localValue.includes(o.value) ? "#398DEF14" : "none",
								minWidth: 128,
							}}
							key={`${index}-${o.value}`}
							onClick={() => toggleSelection(o.value)}
						>
							<Checkbox
								checked={localValue.includes(o.value)}
								tabIndex={-1}
								disableRipple
								inputProps={{ "aria-labelledby": labelId }}
							/>
							<span id={labelId} style={{ whiteSpace: "normal" }}>{o.label}</span>
						</MenuItem>
					);
				})}
				{localValue.length > 0 && (
					<Box
						sx={{
							position: "sticky",
							bottom: 0,
							zIndex: 1,
							bgcolor: "background.paper",
							borderTop: "1px solid",
							borderColor: "divider",
							px: "12px",
							py: "8px",
						}}
					>
						<Stack direction="row" flexWrap="wrap" gap="6px">
							{localValue.map((selected, index) => (
								<Chip
									key={index}
									size="small"
									label={
										options.find((o) => o.value === selected)?.label ?? selected
									}
									onDelete={() => toggleSelection(selected)}
								/>
							))}
						</Stack>
					</Box>
				)}
			</Menu>
		</>
	);
};
